import { Heart } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { useUiStore } from '@/store/uiStore'
import { useIsPropertyLiked, useToggleWishlist } from '@/hooks/api/useWishlist'
import type { PropertyIntent } from '@/types/property'

export function PropertyFavoriteButton({
  propertyId,
  className,
  iconClassName,
  source = 'property_listing',
  cityId,
  areaId,
  categoryId,
  intent,
}: {
  propertyId: number
  className?: string
  iconClassName?: string
  source?: string
  cityId?: number
  areaId?: number
  categoryId?: number
  intent?: PropertyIntent
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const setAuthModalOpen = useUiStore((s) => s.setAuthModalOpen)
  const { liked } = useIsPropertyLiked(propertyId)
  const toggle = useToggleWishlist()
  const tooltip = liked ? 'Remove from wishlist' : 'Add to wishlist'

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!isAuthenticated) {
      setAuthModalOpen(true)
      return
    }

    toggle.mutate({ propertyId, source, cityId, areaId, categoryId, intent })
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={toggle.isPending}
      className={cn(
        'group/fav relative flex items-center justify-center transition-opacity disabled:opacity-60',
        className,
      )}
      aria-label={tooltip}
      aria-pressed={liked ? 'true' : 'false'}
    >
      <span
        role="tooltip"
        className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 whitespace-nowrap rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-md transition-opacity duration-150 group-hover/fav:opacity-100"
      >
        {tooltip}
      </span>
      <Heart
        className={cn(
          'drop-shadow-md',
          liked ? 'fill-rose-500 text-rose-500' : 'fill-black/20 text-white',
          iconClassName,
        )}
      />
    </button>
  )
}
